import React, { useEffect } from 'react';
import { useFormikContext } from 'formik';
import {
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Typography,
} from '@mui/material';
import { constValues, legalPersonType, personType, translations } from '../../lib/consts';
import { preparePersonRelationsList } from '../../lib/helpers';
import LegalEntity from '../LegalEntity';
import LeasingEntity from '../LeasingEntity';
import { IndividualCleanup } from '../Individual/Individual';
import { LegalEntityCleanup } from '../LegalEntity/LegalEntity';
import { LeasingEntityCleanup } from '../LeasingEntity/LeasingEntity';

type LegalProps = {
    legalEntityType: personType;
};

const LegalLeasingEntity = ({ legalEntityType }: LegalProps) => {
    const { values, errors, touched, setFieldValue } = useFormikContext<any>();

    const industrialClassificationType = `${personType[legalEntityType]}IndustrialClassificationType`;
    const legalPersonTypePropertyName = `${personType[legalEntityType]}Type`;
    const submitterRelationId = `${personType[legalEntityType]}LegalEntityPersonRoleId`;

    const handleChangeIndustrialClassificationType = (e: { target: { value: any } }): any => {
        setFieldValue(industrialClassificationType, e.target.value);
        if (e.target.value === constValues.industrialClassificationTypeLeasing) {
            LegalEntityCleanup(legalEntityType, setFieldValue);
        } else if (e.target.value !== '') {
            LeasingEntityCleanup(legalEntityType, setFieldValue);
        } else {
            LegalEntityCleanup(legalEntityType, setFieldValue);
            LeasingEntityCleanup(legalEntityType, setFieldValue);
        }
    };

    const handleChangeSubmitterRelation = (event: SelectChangeEvent<HTMLSelectElement>) => {
        setFieldValue(submitterRelationId, event.target.value);

        if (personType[legalEntityType] === 'owner') {
            IndividualCleanup(personType.user, setFieldValue);
            LegalLeasingCleanup(personType.user, setFieldValue);
            setFieldValue('howIsVehicleUsed', '');
            setFieldValue('submitterUserByVehicleRegistration', '');
            setFieldValue('userType', '');
        }
    };

    const handleChangeLegalPersonType = () => {
        if (values[legalPersonTypePropertyName] === legalPersonType.legal) {
            IndividualCleanup(legalEntityType, setFieldValue);
        }
    };

    useEffect(() => {
        handleChangeLegalPersonType();
    }, [values[legalPersonTypePropertyName]]);

    return (
        <>
            <Typography style={{ fontWeight: 'bold', fontSize: '18px', paddingTop: '8px' }} variant="subtitle1">
                {translations.legalInfo.PERSON}
            </Typography>

            <FormControl>
                <InputLabel id={`${industrialClassificationType}-label`}>
                    {translations.SELECT_INDUSTRIAL_TYPE}
                </InputLabel>

                <Select
                    id={industrialClassificationType}
                    labelId={`${industrialClassificationType}-label`}
                    name={industrialClassificationType}
                    value={values[industrialClassificationType] || ''}
                    label={translations.SELECT_INDUSTRIAL_TYPE}
                    error={!!(touched[industrialClassificationType] && errors[industrialClassificationType])}
                    onChange={handleChangeIndustrialClassificationType}
                >
                    {values['industrialClassifications'].map((option: any) => {
                        return (
                            <MenuItem key={option.id} value={option.id}>
                                {option.text}
                            </MenuItem>
                        );
                    })}
                    <MenuItem key="0" value="0">
                        {translations.OTHER_INDUSTRIAL_CLASSIFICATIONS}
                    </MenuItem>
                </Select>

                {touched[industrialClassificationType] && errors[industrialClassificationType] ? (
                    <FormHelperText error={true}>
                        <>{errors[industrialClassificationType]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            {values[industrialClassificationType] !== '' ? (
                values[industrialClassificationType] !== constValues.industrialClassificationTypeLeasing ? (
                    <LegalEntity legalEntityType={legalEntityType} />
                ) : (
                    <LeasingEntity leasingEntityType={legalEntityType}></LeasingEntity>
                )
            ) : null}

            {legalEntityType !== personType.submitter && values[industrialClassificationType] !== '' && (
                <FormControl>
                    <InputLabel id={`${submitterRelationId}-label`}>
                        {translations.SELECT_LEGAL_PERSON_ROLE_TYPE}
                    </InputLabel>

                    <Select
                        id={submitterRelationId}
                        labelId={`${submitterRelationId}-label`}
                        name={submitterRelationId}
                        value={values[submitterRelationId] || ''}
                        label={translations.SELECT_LEGAL_PERSON_ROLE_TYPE}
                        error={!!(touched[submitterRelationId] && errors[submitterRelationId])}
                        onChange={handleChangeSubmitterRelation}
                    >
                        {preparePersonRelationsList(
                            values.personRelations,
                            legalEntityType,
                            values.submitterType,
                            legalPersonType.legal,
                        )?.map((option: any) => {
                            return (
                                <MenuItem key={option.id} value={option.value}>
                                    {option.text}
                                </MenuItem>
                            );
                        })}
                    </Select>

                    {touched[submitterRelationId] && errors[submitterRelationId] ? (
                        <FormHelperText error={true}>
                            <>{errors[submitterRelationId]}</>
                        </FormHelperText>
                    ) : null}
                </FormControl>
            )}
        </>
    );
};

export const LegalLeasingCleanup = (
    type: personType,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void,
) => {
    setFieldValue(`${personType[type]}IndustrialClassificationType`, '');
    setFieldValue(`${personType[type]}LegalEntityPersonRoleId`, '');
    LegalEntityCleanup(type, setFieldValue);
    LeasingEntityCleanup(type, setFieldValue);
};

export default LegalLeasingEntity;
